import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FiLayers, FiCheckCircle, FiAlertCircle, FiXCircle, FiClock } from "react-icons/fi";
import { toast } from "react-toastify";
import SubscriptionStatsCard from "./SubscriptionStatsCard";
import SubscriptionTable from "./SubscriptionTable";
import CreateSubscriptionModal from "./CreateSubscriptionModal";
import PurchaseSlider from "./PurchaseSlider";
import { getSubscriptionPlans, getSubscriptionPlanDetail, inactivateSubscription, reactivateSubscription } from "../../services/subscriptionService";
import FieldHelpTooltip from "../Help/FieldHelpTooltip";
import { HELP_CONTENT } from "../Help/HelpContent";

const getToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
const getIn15Days = () => { const d = getToday(); d.setDate(d.getDate() + 15); return d; };

const isExpiringSoon = (plan) => {
  if (!plan.endDate || plan.status !== "Active") return false;
  const d = new Date(plan.endDate);
  return d >= getToday() && d <= getIn15Days();
};

const isExpired = (plan) => {
  if (!plan.endDate || plan.status !== "Active") return false;
  return new Date(plan.endDate) < getToday();
};

const FILTER_FN = {
  active:   p => p.status === "Active",
  inactive: p => p.status === "Inactive",
  expiring: isExpiringSoon,
  expired:  isExpired,
};

const SubscriptionDashboard = () => {
  const [allPlans, setAllPlans] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [activeFilter, setActiveFilter] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currency, setCurrency] = useState("AED");
  const [industry, setIndustry] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [editingPlanData, setEditingPlanData] = useState(null);

  useEffect(() => {
    const vendorProfile = JSON.parse(localStorage.getItem("vendorProfile"));
    if (vendorProfile?.currency) setCurrency(vendorProfile.currency);
    if (vendorProfile?.industry !== undefined) setIndustry(vendorProfile.industry);
  }, []);

  const fetchPlans = useCallback(async () => {
    try {
      setTableLoading(true);
      const res = await getSubscriptionPlans({ page: 1, limit: 9999 });
      const rows = Array.isArray(res.data) ? res.data : [];
      setAllPlans(
        rows.map(item => ({
          id: item.id,
          planName: item.name || item.service_offering_name || "-",
          serviceType: item.service_name || "-",
          serviceId: item.service || null,
          type: item.subscription_type || "-",
          pricing: item.pricing || 0,
          paymentType: item.payment_type || "Full",
          recurringAmount: item.recurring_amount || 0,
          recurringDueDay: item.recurring_due_day || null,
          subscriberCount: item.active_subscriber_count ?? 0,
          status: item.status || "Active",
          endDate: item.end_date || null,
        }))
      );
    } catch {
      toast.error("Failed to load subscription plans");
    } finally {
      setTableLoading(false);
    }
  }, []);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  // Tile counts — computed from the full dataset
  const totalCount    = allPlans.length;
  const activeCount   = useMemo(() => allPlans.filter(p => p.status === "Active").length, [allPlans]);
  const inactiveCount = useMemo(() => allPlans.filter(p => p.status === "Inactive").length, [allPlans]);
  const expiringCount = useMemo(() => allPlans.filter(isExpiringSoon).length, [allPlans]);
  const expiredCount  = useMemo(() => allPlans.filter(isExpired).length, [allPlans]);

  // Filter → sort → paginate
  const filteredPlans = useMemo(() => {
    const fn = FILTER_FN[activeFilter];
    return fn ? allPlans.filter(fn) : allPlans;
  }, [allPlans, activeFilter]);

  const sortedPlans = useMemo(() => {
    if (!sortBy) return filteredPlans;
    return [...filteredPlans].sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      const cmp = typeof aVal === "number" ? aVal - bVal : String(aVal).localeCompare(String(bVal));
      return sortOrder === "desc" ? -cmp : cmp;
    });
  }, [filteredPlans, sortBy, sortOrder]);

  const displayedPlans = useMemo(() => {
    const start = (page - 1) * limit;
    return sortedPlans.slice(start, start + limit);
  }, [sortedPlans, page, limit]);

  const handleSort = (col, order) => {
    setSortBy(col);
    setSortOrder(order);
    setPage(1);
  };

  const handleTileFilter = (filter) => {
    if (filter === null) {
      setActiveFilter(null);
    } else {
      setActiveFilter(prev => (prev === filter ? null : filter));
    }
    setPage(1);
  };

  const handleModalSuccess = () => { fetchPlans(); };

  const handleEditClick = async (row) => {
    try {
      const res = await getSubscriptionPlanDetail(row.id);
      setEditingPlanData(res.data || null);
    } catch {
      toast.error("Failed to load plan details");
    }
  };

  const handleInactivate = async (row) => {
    if (!window.confirm(`Are you sure you want to inactivate "${row.planName}"? You can reactivate it later if needed.`)) return;
    try {
      await inactivateSubscription({ id: row.id });
      toast.success("Plan inactivated successfully");
      fetchPlans();
    } catch (err) {
      toast.error(err?.message || "Failed to inactivate plan");
    }
  };

  const handleReactivate = async (row) => {
    try {
      await reactivateSubscription({ id: row.id });
      toast.success("Plan reactivated successfully");
      fetchPlans();
    } catch (err) {
      toast.error(err?.message || "Failed to reactivate plan");
    }
  };

  return (
    <div style={{ padding: "24px", background: "#F3F4F6", minHeight: "100vh", fontFamily: "Inter, sans-serif" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "24px",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <h1 style={{ margin: 0, fontSize: "24px", fontWeight: "700", color: "#111827" }}>
          Subscription Plans
        </h1>
        <button
          data-tour="subscription-create"
          onClick={() => setShowModal(true)}
          style={{
            background: "#C0335A",
            color: "#fff",
            border: "none",
            borderRadius: "9999px",
            padding: "12px 24px",
            fontSize: "14px",
            fontWeight: "600",
            cursor: "pointer",
            transition: "background 0.2s",
          }}
          onMouseEnter={e => e.currentTarget.style.background = "#a52b4e"}
          onMouseLeave={e => e.currentTarget.style.background = "#C0335A"}
        >
          + Create New Plan
        </button>
      </div>

      {/* Stats tiles */}
      <div
        data-tour="subscription-stats"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "16px",
          marginBottom: "24px",
        }}
      >
        <SubscriptionStatsCard
          icon={<FiLayers />}
          label={<>Total Plans <FieldHelpTooltip {...HELP_CONTENT.subscription.totalPlans} /></>}
          value={totalCount}
          loading={tableLoading}
          onClick={() => handleTileFilter(null)}
          isActive={activeFilter === null}
        />
        <SubscriptionStatsCard
          icon={<FiCheckCircle />}
          label={<>Active Plans <FieldHelpTooltip {...HELP_CONTENT.subscription.activePlans} /></>}
          value={activeCount}
          loading={tableLoading}
          onClick={() => handleTileFilter("active")}
          isActive={activeFilter === "active"}
        />
        <SubscriptionStatsCard
          icon={<FiAlertCircle />}
          label={<>Expiring in 15 Days <FieldHelpTooltip {...HELP_CONTENT.subscription.expiringPlans} /></>}
          value={expiringCount}
          loading={tableLoading}
          onClick={() => handleTileFilter("expiring")}
          isActive={activeFilter === "expiring"}
        />
        <SubscriptionStatsCard
          icon={<FiClock />}
          label={<>Expired Plans <FieldHelpTooltip {...HELP_CONTENT.subscription.expiredPlans} /></>}
          value={expiredCount}
          loading={tableLoading}
          onClick={() => handleTileFilter("expired")}
          isActive={activeFilter === "expired"}
        />
        <SubscriptionStatsCard
          icon={<FiXCircle />}
          label={<>Inactive Plans <FieldHelpTooltip {...HELP_CONTENT.subscription.inactivePlans} /></>}
          value={inactiveCount}
          loading={tableLoading}
          onClick={() => handleTileFilter("inactive")}
          isActive={activeFilter === "inactive"}
        />
      </div>

      {/* Plans table */}
      <div
        data-tour="subscription-table"
        style={{
          background: "#fff",
          borderRadius: "12px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          overflow: "hidden",
          paddingBottom: "16px",
        }}
      >
        <SubscriptionTable
          data={displayedPlans}
          loading={tableLoading}
          page={page}
          limit={limit}
          total={filteredPlans.length}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          onPageChange={setPage}
          onLimitChange={(newLimit) => { setLimit(newLimit); setPage(1); }}
          currency={currency}
          onPurchase={(plan) => setSelectedPlan(plan)}
          onEdit={handleEditClick}
          onInactivate={handleInactivate}
          onReactivate={handleReactivate}
        />
      </div>

      {showModal && (
        <CreateSubscriptionModal
          onClose={() => setShowModal(false)}
          onSuccess={handleModalSuccess}
          currency={currency}
          industry={industry}
        />
      )}

      {editingPlanData && (
        <CreateSubscriptionModal
          onClose={() => setEditingPlanData(null)}
          onSuccess={() => { setEditingPlanData(null); handleModalSuccess(); }}
          currency={currency}
          isEditMode={true}
          initialData={editingPlanData}
          industry={industry}
        />
      )}

      {selectedPlan && (
        <PurchaseSlider
          plan={selectedPlan}
          currency={currency}
          industry={industry}
          onClose={() => { setSelectedPlan(null); fetchPlans(); }}
        />
      )}
    </div>
  );
};

export default SubscriptionDashboard;
